import { AssignmentTurnedIn, Edit } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import StoreHistory from '../../components/History/StoreHistory';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';

import EditStore from './EditStore';
import Inventory from './Inventory';
import MoveItems from './MoveItems';

type Row = DbDocument<Store>;

const StoresList = () => {
  const navidate = useNavigate();
  const { lang } = useLocale();
  const { stores, worker } = useCafe();
  const [open, setOpen] = useState(false);
  const [openInventory, setOpenInventory] = useState(false);
  const [openMove, setOpenMove] = useState(false);
  const [store, setStore] = useState<DbDocument<Store> | null>(null);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.name}</Typography>,
        search: true,
      },
      {
        name: 'inventory',
        title: '',
        render: (r: Row) => (
          <PrimaryButton
            variant="outlined"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setStore(r);
              setOpenInventory(true);
            }}
            startIcon={<AssignmentTurnedIn />}
            disabled={!worker?.credentials.conduct_inventory}
          >
            {t.inventory[lang]}
          </PrimaryButton>
        ),
      },
      {
        name: 'history',
        title: t.history[lang],
        align: 'center',
        render: (r: Row) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <StoreHistory store={r} />
          </Box>
        ),
      },
      {
        name: 'edit',
        title: t.edit[lang],
        render: (r: Row) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setStore(r);
              setOpen(true);
            }}
            disabled={!worker?.credentials.edit_stores}
          >
            <Edit />
          </IconButton>
        ),
      },
    ],
    [lang, worker?.credentials.conduct_inventory, worker?.credentials.edit_stores]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto' }}>
      <DataTable
        columns={columns}
        rows={stores}
        onRowClick={(r) => navidate(r.id)}
        dataTestIdPrefix="store"
        actionButton={
          stores.length > 1 ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <PrimaryButton onClick={() => setOpenMove(true)} variant="contained">
                {t.move[lang]}
              </PrimaryButton>
              <PrimaryButton
                onClick={() => setOpen(true)}
                variant="contained"
                disabled={!worker?.credentials.edit_stores}
              >
                {t.add_store[lang]}
              </PrimaryButton>
            </Box>
          ) : (
            <PrimaryButton
              onClick={() => setOpen(true)}
              variant="contained"
              disabled={!worker?.credentials.edit_stores}
            >
              {t.add_store[lang]}
            </PrimaryButton>
          )
        }
        customHeader={
          <PrimaryButton onClick={() => navidate('all')} variant="text" color="secondary">
            {t.residues_in_all_stores[lang]}
          </PrimaryButton>
        }
      />

      {open && (
        <EditStore
          store={store}
          open={open}
          onClose={() => {
            setOpen(false);
            setStore(null);
          }}
        />
      )}

      {openInventory && store && (
        <Inventory
          store={store}
          onClose={() => {
            setOpenInventory(false);
            setStore(null);
          }}
        />
      )}

      {openMove && <MoveItems onClose={() => setOpenMove(false)} />}
    </Box>
  );
};

export default StoresList;
